import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { productService } from '../../../services/productService';
import type { ProductAvailability } from '../../../types/stock';

interface ComparisonRow {
  stockId: number;
  stockName: string;
  quantityByProductId: Map<number, number>;
  hasAllProducts: boolean;
}

function buildComparisonRows(availabilities: ProductAvailability[]): ComparisonRow[] {
  const productIds = availabilities.map((a) => a.productId);
  const stockRows = new Map<number, ComparisonRow>();

  for (const availability of availabilities) {
    for (const dist of availability.stockDistribution) {
      const row = stockRows.get(dist.stockId) ?? {
        stockId: dist.stockId,
        stockName: dist.stockName,
        quantityByProductId: new Map<number, number>(),
        hasAllProducts: false,
      };
      row.quantityByProductId.set(availability.productId, dist.quantity);
      stockRows.set(dist.stockId, row);
    }
  }

  const rows = Array.from(stockRows.values()).map((row) => ({
    ...row,
    hasAllProducts: productIds.every((productId) => (row.quantityByProductId.get(productId) ?? 0) > 0),
  }));

  rows.sort((a, b) => {
    if (a.hasAllProducts !== b.hasAllProducts) {
      return a.hasAllProducts ? -1 : 1;
    }
    return a.stockName.localeCompare(b.stockName);
  });

  return rows;
}

export default function ProductAvailabilityComparePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyId = id ? parseInt(id) : null;

  const productIds = (searchParams.get('productIds') ?? '')
    .split(',')
    .map((productId) => parseInt(productId))
    .filter((productId) => !isNaN(productId));

  const [availabilities, setAvailabilities] = useState<ProductAvailability[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!companyId || productIds.length === 0) {
        setError('Не вибрано жодного продукту для порівняння');
        setLoading(false);
        return;
      }

      setLoading(true);
      // productService.getAvailability never throws — it logs and resolves to [] on failure.
      const data = await productService.getAvailability(companyId, productIds);
      setAvailabilities(data);
      setLoading(false);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, productIds.join(',')]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Box>
    );
  }

  const rows = buildComparisonRows(availabilities);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/products`)}
          size="small"
        >
          Назад
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Порівняння наявності
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Склади, де є всі вибрані продукти, показано першими та позначено {' '}
        <DoneAllIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} />.
      </Typography>

      {rows.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 6 },
            textAlign: 'center',
            backgroundColor: 'background.default',
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Жоден склад не містить вибраних продуктів
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table component={Paper}>
            <TableHead>
              <TableRow>
                <TableCell>Склад</TableCell>
                {availabilities.map((availability) => (
                  <TableCell key={availability.productId} align="center">
                    {availability.productName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.stockId}
                  sx={row.hasAllProducts ? { backgroundColor: (theme) => alpha(theme.palette.success.main, 0.12) } : undefined}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {row.hasAllProducts && (
                        <DoneAllIcon fontSize="small" color="success" />
                      )}
                      {row.stockName}
                    </Box>
                  </TableCell>
                  {availabilities.map((availability) => {
                    const quantity = row.quantityByProductId.get(availability.productId) ?? 0;
                    return (
                      <TableCell key={availability.productId} align="center">
                        <Chip
                          label={`${quantity} шт`}
                          size="small"
                          color={quantity > 0 ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
